import React from 'react';
import styled from 'styled-components';

const Project = ({
    project_name,
    project_description,
    project_tech_stack,
    project_url,
    project_img,
    project_video_id,
    project_video_src,
    project_youtube_channel,
    project_linkedin,
    project_instagram,
    project_tiktok,
    project_github,
    project_chrome_store,
}) => {
    const primary_url = project_url || project_github;
    return (
        <ProjectCard>
            <ProjectMedia>
                {project_video_id ? (
                    <VideoFrame
                        src={`https://www.youtube-nocookie.com/embed/${project_video_id}?rel=0&modestbranding=1`}
                        title={`${project_name} demo video`}
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                    />
                ) : project_video_src ? (
                    <Video
                        src={project_video_src}
                        controls
                        playsInline
                        muted
                        loop
                        preload="metadata"
                    />
                ) : (
                    <img src={project_img} alt="" loading="lazy" />
                )}
            </ProjectMedia>
            <ProjectContent>
                <ProjectTitle>
                    <TitleLink
                        href={primary_url}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {project_name}
                        <ExternalIcon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M7 17L17 7M17 7H8M17 7v9" />
                        </ExternalIcon>
                    </TitleLink>
                </ProjectTitle>
                <ProjectDescription>{project_description}</ProjectDescription>

                <SocialRow>
                    {project_url && (
                        <SocialPill
                            href={project_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Website"
                        >
                            <SocialIcon viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.6.08-1.18.21-1.74L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                            </SocialIcon>
                            Website
                        </SocialPill>
                    )}
                    {project_chrome_store && (
                        <SocialPill
                            href={project_chrome_store}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Chrome Web Store listing"
                        >
                            <SocialIcon viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M12 0C7.79 0 4.084 2.17 1.946 5.45l5.04 8.73a5.5 5.5 0 0 1 5.014-7.68h9.587A12 12 0 0 0 12 0zm11.49 6.5h-9.79a5.5 5.5 0 0 1 4.79 8.225L13.7 23.94A12 12 0 0 0 24 12c0-1.94-.46-3.77-1.27-5.4-.42-.06-.83-.1-1.24-.1zM12 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9zM1.36 6.81A12 12 0 0 0 0 12c0 6.34 4.92 11.53 11.15 11.96l4.85-8.4a5.5 5.5 0 0 1-9.6-.31L1.36 6.81z" />
                            </SocialIcon>
                            Chrome Web Store
                        </SocialPill>
                    )}
                    {project_github && (
                        <SocialPill
                            href={project_github}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="GitHub repository"
                        >
                            <SocialIcon viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.385-1.335-1.755-1.335-1.755-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.4 3-.405 1.02.005 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                            </SocialIcon>
                            GitHub
                        </SocialPill>
                    )}
                    {project_youtube_channel && (
                        <SocialPill
                            href={project_youtube_channel}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="YouTube channel"
                        >
                            <SocialIcon viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                            </SocialIcon>
                            YouTube
                        </SocialPill>
                    )}
                    {project_linkedin && (
                        <SocialPill
                            href={project_linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="LinkedIn page"
                        >
                            <SocialIcon viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                            </SocialIcon>
                            LinkedIn
                        </SocialPill>
                    )}
                    {project_instagram && (
                        <SocialPill
                            href={project_instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram profile"
                        >
                            <SocialIcon viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.974.975 1.246 2.242 1.308 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.975.974-2.242 1.246-3.608 1.308-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.974-.975-1.246-2.242-1.308-3.608C2.175 15.584 2.163 15.204 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608.975-.974 2.242-1.246 3.608-1.308C8.416 2.175 8.796 2.163 12 2.163zm0-2.163C8.741 0 8.332.014 7.052.072 5.773.13 4.548.396 3.515 1.43 2.481 2.463 2.215 3.688 2.157 4.967.099 6.247.085 6.656.085 9.915v4.17c0 3.259.014 3.668.072 4.948.058 1.279.324 2.504 1.358 3.537 1.033 1.034 2.258 1.3 3.537 1.358 1.28.058 1.689.072 4.948.072h4.17c3.259 0 3.668-.014 4.948-.072 1.279-.058 2.504-.324 3.537-1.358 1.034-1.033 1.3-2.258 1.358-3.537.058-1.28.072-1.689.072-4.948v-4.17c0-3.259-.014-3.668-.072-4.948-.058-1.279-.324-2.504-1.358-3.537C21.452.396 20.227.13 18.948.072 17.668.014 17.259 0 14 0h-2zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                            </SocialIcon>
                            Instagram
                        </SocialPill>
                    )}
                    {project_tiktok && (
                        <SocialPill
                            href={project_tiktok}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="TikTok profile"
                        >
                            <SocialIcon viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43V8.36a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1.84-.79z" />
                            </SocialIcon>
                            TikTok
                        </SocialPill>
                    )}
                </SocialRow>

                <TechStack>{project_tech_stack}</TechStack>
            </ProjectContent>
        </ProjectCard>
    );
};

export default Project;

const ProjectCard = styled.article`
    position: relative;
    display: flex;
    flex-direction: column;
    background: var(--bg-elevated);
    border-radius: var(--radius-lg);
    overflow: hidden;
    border: 1px solid var(--border);
    transition: border-color var(--transition), transform var(--transition),
        background var(--transition);
    height: 100%;

    &:hover {
        border-color: var(--border-strong);
        transform: translateY(-3px);
        background: var(--bg-panel);
    }
`;

const ProjectMedia = styled.div`
    position: relative;
    z-index: 2;
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    background: var(--bg-panel);
    border-bottom: 1px solid var(--border);

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 400ms var(--ease);
    }

    ${ProjectCard}:hover & img {
        transform: scale(1.04);
    }
`;

const VideoFrame = styled.iframe`
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
`;

const Video = styled.video`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    background: var(--bg-panel);
`;

const ProjectContent = styled.div`
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: 0.75rem;
`;

const ProjectTitle = styled.h3`
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--text);
    margin: 0;
    letter-spacing: -0.01em;
`;

const TitleLink = styled.a`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    color: inherit;
    text-decoration: none;

    /* Whole-card click target — sits behind interactive elements (z-index 2) */
    &::after {
        content: '';
        position: absolute;
        inset: 0;
        z-index: 1;
    }
`;

const ExternalIcon = styled.svg`
    width: 16px;
    height: 16px;
    color: var(--text-subtle);
    flex-shrink: 0;
    transition: color var(--transition), transform var(--transition);

    ${ProjectCard}:hover & {
        color: var(--accent);
        transform: translate(2px, -2px);
    }
`;

const ProjectDescription = styled.p`
    font-size: 0.9375rem;
    color: var(--text-muted);
    line-height: 1.65;
    margin: 0;
    flex-grow: 1;
`;

const SocialRow = styled.div`
    position: relative;
    z-index: 2;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

const SocialPill = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--text-muted);
    background: var(--bg-panel);
    border: 1px solid var(--border);
    padding: 0.4rem 0.75rem;
    border-radius: var(--radius-full);
    text-decoration: none;
    transition: color var(--transition), border-color var(--transition),
        background var(--transition);

    &:hover {
        color: var(--accent);
        border-color: var(--accent-border);
        background: var(--accent-soft);
    }
`;

const SocialIcon = styled.svg`
    width: 13px;
    height: 13px;
    flex-shrink: 0;
`;

const TechStack = styled.p`
    font-size: 0.75rem;
    color: var(--text-subtle);
    font-family: var(--font-mono);
    margin: 0;
    padding-top: 0.875rem;
    border-top: 1px solid var(--border);
    letter-spacing: 0.01em;
`;
